import { createRootRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { lazy, Suspense, useState } from "react";
import { useAuthStore, useOrgStore } from "@/stores";
import { NotFoundPage } from "@/components/error-pages/not-found-page";
import { ServerErrorPage } from "@/components/error-pages/server-error-page";
import { SkipLink } from "@/components/skip-link/skip-link";
import { KeyboardShortcutsCheatsheet } from "@/components/keyboard-shortcuts/keyboard-shortcuts-cheatsheet";
import { useGlobalShortcut } from "@/lib/hooks/use-global-shortcut";
import { useFeatureEnabled } from "@/features/features";

const enableTanStackDevtools =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_TANSTACK_DEVTOOLS === "true";

// Router devtools stay opt-in so local debug affordances do not cover app UI.
const TanStackRouterDevtools = enableTanStackDevtools
  ? lazy(() =>
      import("@tanstack/router-devtools").then((m) => ({
        default: m.TanStackRouterDevtools,
      })),
    )
  : () => null;

// Boot hydration: hydrate auth + org once before any child route resolves.
// The stores' internal `isInitialized` + `isLoading` flags collapse concurrent
// invocations so reload + navigation never trigger duplicate /api/auth/me.
export const Route = createRootRoute({
  beforeLoad: async () => {
    const auth = useAuthStore.getState();
    if (!auth.isInitialized && !auth.isLoading) {
      await auth.hydrate();
    }
    const org = useOrgStore.getState();
    if (!org.isInitialized && !org.isLoading && useAuthStore.getState().isAuthenticated) {
      await org.hydrate();
    }
  },
  component: RootLayout,
  notFoundComponent: NotFoundPage,
  errorComponent: ({ error }) => (
    <ServerErrorPage error={error instanceof Error ? error : undefined} />
  ),
});

function RootLayout() {
  const [cheatsheetOpen, setCheatsheetOpen] = useState(false);
  const navigate = useNavigate();
  const workflowsEnabled = useFeatureEnabled("workflows");

  // "?" opens the shortcut cheatsheet. shift+slash is the physical key combo.
  useGlobalShortcut("shift+slash", () => setCheatsheetOpen(true));

  // cmd+k → opens GlobalSearchModal. AppHeader owns the keydown listener for
  // it; this file used to also hook mod+k to a "coming soon" toast which
  // double-fired alongside the real handler. Removed once GlobalSearchModal
  // shipped.

  // g w → navigate to /workflows.
  useGlobalShortcut("g+w", () => {
    if (workflowsEnabled) void navigate({ to: "/workflows" });
  });

  // g c → navigate to /checkpoints.
  useGlobalShortcut("g+c", () => {
    if (workflowsEnabled) void navigate({ to: "/checkpoints" });
  });

  return (
    <>
      {/* Skip-link renders above everything; only visible on keyboard focus */}
      <SkipLink />
      <Outlet />
      <KeyboardShortcutsCheatsheet
        isOpen={cheatsheetOpen}
        onClose={() => setCheatsheetOpen(false)}
      />
      {enableTanStackDevtools && (
        <Suspense fallback={null}>
          <TanStackRouterDevtools position="bottom-right" />
        </Suspense>
      )}
    </>
  );
}
